import ChevronDown from '@mui/icons-material/KeyboardArrowDown';
import Collapse from '@mui/material/Collapse';
import { Button } from 'components/Shared/Inputs';
import React, { type FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CCCNotesSection.scss';
import { type IActivityDetails } from 'components/CaseManagement/interfaces';
import { useSessionFlag } from 'hooks/useSessionFlag';

export const CCCNotesSection: FunctionComponent<{ activity: IActivityDetails }> = ({ activity }) => {
    const { t } = useTranslation(['activityDetails'], { useSuspense: false });
    const useDummyData = useSessionFlag<boolean>('use-fake-ccc-notes');
    const [outerExpanded, setOuterExpanded] = useState(false);
    const [openInnerSections, setOpenInnerSections] = useState<Record<number, boolean>>({});

    const handleOuterClick = () => setOuterExpanded((prev) => !prev);

    const handleInnerClick = (index: number) => {
        setOpenInnerSections((prev) => ({
            ...prev,
            [index]: !prev[index]
        }));
    };
    const fakeNotes = '<p>Clt stated that they were previously in therapy through their insurance provider, Spring Health, but were dissatisfied with the service. Clt reported   that the therapy sessions were ineffective, as the therapists did not address Clt\'s underlying traumas or the reasons behind their recent nervous   breakdowns at work. Instead, Clt felt the therapists simply "patted them on   the back" and validated their feelings without providing any meaningful therapeutic process. Clt has now filed a formal request to be reimbursed for   the payments made and to have the remaining billing dismissed, as the therapy sessions led them nowhere and they are currently under significant financial strain.</p>';
    const fakeRisks = ['risk_usa_clinical_impacts_on_job', 'risk_domestic_violence', 'risk_addictions'];
    const fakeGuidelines =  '<h3>Test Test</h3><p>Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test</p><p>Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test</p><p>Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test Test</p>';
    
    let cccNotes = useDummyData ? fakeNotes : activity.serviceForms.cac_Notes || '';
    let risks =  useDummyData ? fakeRisks : activity.serviceForms.risks || [];  
    if (risks.length > 0) {
        cccNotes +=  `<div class="risks_identified_container">
                       <p class="risks_identified_header">${t('risks_identified')}</p>
                       <ul data-testid="risks_identified_list">`;
        for (const risk of risks) {
            cccNotes += `<li data-testid="risks_identified_list-item">${t(risk)}</li>`;
        }
        cccNotes +=   `</ul>
                      </div>`;
    }
    const guidelines = useDummyData ? fakeGuidelines : activity.serviceForms.cacGuidelines || '';

    const innerSections = [
        { label: t('ccc_notes'), content: cccNotes },
        { label: t('cac_recommended_guidelines'), content: guidelines }
    ];

    return (
        (cccNotes || guidelines) && (
            <div className="notes_section_wrapper">
                {/* Outer collapsible header */}
                <div className="ccc_notes_section" onClick={handleOuterClick}>
                    <div className="ccc_notes_collapse_button">
                        <Button aria-label="expand outer row" variant="text" round size="medium">
                            <ChevronDown
                                className="collapse_indicator"
                                style={{ transform: outerExpanded ? 'rotate(180deg)' : 'rotate(360deg)' }}
                            />
                        </Button>
                    </div>
                    <p>{t('ccc_intervention_notes')}</p>
                </div>

                {/* Outer Collapse — controls visibility of all inner sections */}
                <Collapse in={outerExpanded} timeout="auto" sx={outerExpanded ? { marginBottom: '10px' } : undefined}>
                    {innerSections.map((section, i) => (
                        <React.Fragment key={i}>
                            {/* Each inner section header is independently clickable */}
                            <div className="notes_heading" onClick={() => handleInnerClick(i)}>
                                <div className="ccc_notes_collapse_button">
                                    <Button aria-label={`expand inner row ${i}`} variant="text" round size="medium">
                                        <ChevronDown
                                            className="collapse_indicator"
                                            style={{
                                                transform: !!openInnerSections[i] ? 'rotate(180deg)' : 'rotate(360deg)'
                                            }}
                                        />
                                    </Button>
                                </div>
                                <p>{section.label}</p>
                            </div>

                            {/* Each inner Collapse has its own independent state */}
                            <Collapse in={!!openInnerSections[i]} timeout="auto">
                                <p className="notes_details" dangerouslySetInnerHTML={{ __html: section.content || '' }} />
                            </Collapse>
                        </React.Fragment>
                    ))}
                </Collapse>
            </div>
        )
    );
};
